import React, { useEffect, useRef, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Skeleton, Loader } from '../ui';
import SectionCard from './SectionCard';
import QuestionCard from './QuestionCard';
import InsertAnchor from './InsertAnchor';
import { useBuilderEditor } from '../../features/builder/BuilderEditorContext';

const SortableQuestion = ({ field, index, sortableId, sectionId, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: { type: 'field', sectionId, fieldId: field.id },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <QuestionCard
        field={field}
        index={index}
        dragHandleProps={{ ...attributes, ...listeners }}
        {...props}
      />
    </div>
  );
};

const findSectionById = (sectionId, sections) => {
  for (const section of sections || []) {
    if (section.id === sectionId) return section;
    const sub = findSectionById(sectionId, section.subSections);
    if (sub) return sub;
  }
  return null;
};

const findParentSectionId = (sectionId, sections) => {
  for (const section of sections || []) {
    if (section.subSections?.some((s) => s.id === sectionId)) return section.id;
    const parentId = findParentSectionId(sectionId, section.subSections);
    if (parentId) return parentId;
  }
  return null;
};

const getAncestorSectionIds = (sectionId, sections) => {
  const ids = new Set();
  let currentId = sectionId;
  while (currentId != null) {
    ids.add(currentId);
    currentId = findParentSectionId(currentId, sections);
  }
  return ids;
};

const FormCanvas = ({
  selectedFieldId,
  selectedSectionId,
  onSelectField,
  isLoading,
}) => {
  const {
    formdata, formsectionsdata,
    editField, selectQuestion, duplicateField, deleteField,
    copyFieldToClipboard, openMoveFieldModal, openMoveSectionModal,
    editSectionModal, deleteSection, duplicateSection, handleFieldDrop,
    languages, submitlanguage, changeLanguage, setUpdateForm,
    insertQuestionAt, insertSectionAt, insertSubsectionAt,
    getSectionDetails, setIsSubsection,
  } = useBuilderEditor();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const canvasRef = useRef(null);

  const getFieldsForSection = useCallback((sectionId) => {
    if (sectionId === 0) return formdata?.fields || [];
    if (formsectionsdata?.id === sectionId && formsectionsdata?.fields) {
      return formsectionsdata.fields;
    }
    const section = findSectionById(sectionId, formdata?.sections);
    return section?.fields || [];
  }, [formdata, formsectionsdata]);

  const expandedSectionIds = selectedSectionId != null && !selectedFieldId
    ? getAncestorSectionIds(selectedSectionId, formdata?.sections)
    : new Set();

  useEffect(() => {
    const scrollToTarget = (selector) => {
      const el = canvasRef.current?.querySelector(selector);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    const timers = [];
    const attemptScroll = () => {
      if (selectedFieldId) {
        return scrollToTarget(`[data-field-id="${selectedFieldId}"]`);
      }
      if (selectedSectionId != null && selectedSectionId !== undefined) {
        return scrollToTarget(`[data-section-id="${selectedSectionId}"]`);
      }
      return false;
    };

    let retryCount = 0;
    const scheduleRetry = (delay) => {
      if (retryCount >= 4) return;
      retryCount += 1;
      timers.push(setTimeout(() => {
        if (!attemptScroll()) scheduleRetry(200);
      }, delay));
    };

    const frame = requestAnimationFrame(() => {
      if (!attemptScroll()) scheduleRetry(150);
    });
    timers.push(frame);

    return () => {
      timers.forEach((id) => {
        cancelAnimationFrame(id);
        clearTimeout(id);
      });
    };
  }, [selectedFieldId, selectedSectionId, formdata]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;
    if (!activeData || !overData || activeData.type !== 'field' || overData.type !== 'field') return;
    if (activeData.sectionId !== overData.sectionId) return;

    const fields = getFieldsForSection(activeData.sectionId);
    const activeIndex = fields.findIndex((f) => f.id === activeData.fieldId);
    const overIndex = fields.findIndex((f) => f.id === overData.fieldId);
    if (activeIndex < 0 || overIndex < 0) return;

    let droppedOver;
    if (activeIndex < overIndex) {
      droppedOver = fields[overIndex];
    } else if (overIndex === 0) {
      droppedOver = { id: 0 };
    } else {
      droppedOver = fields[overIndex - 1];
    }

    handleFieldDrop(fields[activeIndex], droppedOver, activeData.sectionId);
  };

  const renderQuestionCard = (sectionId) => (field, i) => (
    <SortableQuestion
      key={field.id ?? `field-fallback-${i}`}
      sortableId={field.id ?? `field-fallback-${i}`}
      sectionId={sectionId}
      field={field}
      index={i}
      isSelected={selectedFieldId === field.id}
      onSelect={(f) => {
        onSelectField(f);
        selectQuestion(f, sectionId);
      }}
      onDuplicate={(f) => duplicateField(f, sectionId)}
      onDelete={(f) => deleteField(f, sectionId)}
      onCopy={copyFieldToClipboard}
      onMove={(f) => openMoveFieldModal(f, sectionId)}
    />
  );

  const handleSectionSelect = (section, isSub, onLoaded) => {
    setIsSubsection(isSub);
    getSectionDetails(section.id, null, submitlanguage, onLoaded);
  };

  const renderSubsections = (subSections, parentSectionId) => {
    if (!subSections?.length) return null;
    return (
      <div className="builder-document__subsections">
        {subSections.map((sub, i) => (
          <React.Fragment key={sub.id}>
            <InsertAnchor
              anchorId={`subsection-before-${sub.id}`}
              options={['subsection']}
              onInsertSubsection={() => insertSubsectionAt(parentSectionId, i === 0 ? 0 : subSections[i - 1].id)}
            />
            <SectionCard
              section={{ ...sub, sectionAvailable: true, fields: sub.fields || [] }}
              isSubsection
              isSectionSelected={selectedSectionId === sub.id && !selectedFieldId}
              forceExpanded={expandedSectionIds.has(sub.id)}
              selectedFieldId={selectedFieldId}
              onSelectField={(field) => {
                onSelectField(field);
                selectQuestion(field, sub.id);
              }}
              onEditSection={() => {
                handleSectionSelect(sub, true, (data) => editSectionModal(data, true));
              }}
              onDeleteSection={() => deleteSection(sub.id)}
              onDuplicateSection={() => duplicateSection(sub)}
              onDuplicateField={duplicateField}
              onDeleteField={deleteField}
              onCopyField={copyFieldToClipboard}
              onMoveField={(f) => openMoveFieldModal(f, sub.id)}
              onMoveSection={(s) => openMoveSectionModal(s)}
              onInsertQuestion={insertQuestionAt}
              renderQuestionCard={renderQuestionCard(sub.id)}
              renderAfterBody={() => renderSubsections(sub.subSections, sub.id)}
            />
          </React.Fragment>
        ))}
        <InsertAnchor
          anchorId={`subsection-after-${parentSectionId}`}
          options={['subsection']}
          onInsertSubsection={() => {
            const lastId = subSections[subSections.length - 1]?.id ?? 0;
            insertSubsectionAt(parentSectionId, lastId);
          }}
        />
      </div>
    );
  };

  const renderSectionBlock = (section) => {
    const fieldIds = (section.fields || []).map((f, i) => f.id ?? `field-fallback-${section.id}-${i}`);

    return (
      <div key={section.id} className="builder-document__section-block">
        <SectionCard
          section={{ ...section, sectionAvailable: true }}
          selectedFieldId={selectedFieldId}
          isSectionSelected={selectedSectionId === section.id && !selectedFieldId}
          forceExpanded={expandedSectionIds.has(section.id)}
          isSubsection={false}
          onSelectField={(field) => {
            onSelectField(field);
            selectQuestion(field, section.id);
          }}
          onEditSection={() => {
            handleSectionSelect(section, false, (data) => editSectionModal(data, false));
          }}
          onDeleteSection={() => deleteSection(section.id)}
          onDuplicateSection={() => duplicateSection(section)}
          onDuplicateField={duplicateField}
          onDeleteField={deleteField}
          onCopyField={copyFieldToClipboard}
          onMoveField={(f) => openMoveFieldModal(f, section.id)}
          onMoveSection={(s) => openMoveSectionModal(s)}
          onInsertQuestion={insertQuestionAt}
          onInsertSubsection={() => {
            const subs = section.subSections || [];
            const prevId = subs.length ? subs[subs.length - 1].id : 0;
            insertSubsectionAt(section.id, prevId);
          }}
          renderQuestionCard={renderQuestionCard(section.id)}
          renderAfterBody={() => renderSubsections(section.subSections, section.id)}
        />
      </div>
    );
  };

  const renderTopLevelSections = () => {
    const sections = formdata?.sections || [];
    if (!sections.length) return null;

    return sections.map((section, i) => (
      <React.Fragment key={section.id}>
        <InsertAnchor
          anchorId={`section-before-${section.id}`}
          options={['section']}
          onInsertSection={() => insertSectionAt(0, i === 0 ? 0 : sections[i - 1].id)}
        />
        {renderSectionBlock(section)}
      </React.Fragment>
    ));
  };

  if (isLoading) {
    return (
      <div className="builder-canvas">
        <div className="builder-canvas__inner">
          <Skeleton variant="title" />
          <Skeleton variant="card" style={{ marginTop: 16 }} />
          <Skeleton variant="card" style={{ marginTop: 16 }} />
        </div>
      </div>
    );
  }

  if (!formdata) {
    return (
      <div className="builder-canvas">
        <Loader label="Loading form…" />
      </div>
    );
  }

  const rootFields = formdata.fields || [];
  const hasSections = (formdata.sections || []).length > 0;
  const isEmpty = !rootFields.length && !hasSections;

  return (
    <div className="builder-canvas" ref={canvasRef}>
      <div className="builder-canvas__inner">
        <div className="builder-canvas__form-header">
          <div className="builder-canvas__lang-row">
            {languages?.length > 0 && (
              <select
                className="builder-ui-select w-auto"
                value={submitlanguage || ''}
                onChange={(e) => changeLanguage(e.target.value)}
                aria-label="Form language"
              >
                {languages.map((lang) => (
                  <option key={lang.id} value={lang.id}>{lang.name}</option>
                ))}
              </select>
            )}
          </div>
          <h1>{formdata.name}</h1>
          {formdata.description && <p className="builder-text-secondary">{formdata.description}</p>}
          <button type="button" className="builder-canvas__rename-btn" onClick={() => setUpdateForm(true)}>
            Rename form
          </button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="builder-document">
            {isEmpty && (
              <div className="builder-document__empty">
                <InsertAnchor
                  anchorId="form-empty"
                  options={['question', 'section']}
                  onInsertQuestion={() => insertQuestionAt(0, 0)}
                  onInsertSection={() => insertSectionAt(0, 0)}
                  isActive
                />
              </div>
            )}

            {rootFields.length > 0 && (
              <SectionCard
                fields={rootFields}
                sectionId={0}
                isSectionSelected={selectedSectionId === 0 && !selectedFieldId}
                forceExpanded
                selectedFieldId={selectedFieldId}
                onSelectField={(field) => {
                  onSelectField(field);
                  editField(field, 0);
                }}
                onDuplicateField={duplicateField}
                onDeleteField={deleteField}
                onCopyField={copyFieldToClipboard}
                onMoveField={(f) => openMoveFieldModal(f, 0)}
                onInsertQuestion={insertQuestionAt}
                renderQuestionCard={renderQuestionCard(0)}
              />
            )}

            {renderTopLevelSections()}

            {hasSections && (
              <InsertAnchor
                anchorId="section-trailing"
                options={['section']}
                onInsertSection={() => {
                  const sections = formdata.sections || [];
                  const lastId = sections.length ? sections[sections.length - 1].id : 0;
                  insertSectionAt(0, lastId);
                }}
              />
            )}
          </div>
        </DndContext>
      </div>
    </div>
  );
};

export default FormCanvas;
